import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getDisplayText } from '../utils/display'
import type { ApiFetch, Transaction } from '../types'
import {
  assertFinancialPatchAllowed,
  isCancelStatusValue,
} from '../utils/financialPatchGuard'

export interface PatchOp {
  recordId: string
  field: string
  value: string
}

export function applyOptimisticPatch(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
  workerId: string,
  { recordId, field, value }: PatchOp,
) {
  const targetId = getDisplayText(recordId)
  const removeCancelled =
    field === 'status' && isCancelStatusValue(value)

  queryClient.setQueriesData({ queryKey: ['transactions', workerId, workspaceId] }, (old: any) => {
    if (!old) return old
    return {
      ...old,
      pages: old.pages.map((page: any) => {
        const data = Array.isArray(page?.data) ? page.data : []
        if (removeCancelled) {
          const nextData = data.filter(
            (record: Transaction) => getDisplayText(record.record_id) !== targetId,
          )
          if (nextData.length === data.length) return page
          const removed = data.length - nextData.length
          return {
            ...page,
            data: nextData,
            // Keep summary counts roughly in sync until refetch settles.
            ...(typeof page.totalCount === 'number'
              ? { totalCount: Math.max(0, page.totalCount - removed) }
              : {}),
            ...(typeof page.total === 'number'
              ? { total: Math.max(0, page.total - removed) }
              : {}),
          }
        }
        return {
          ...page,
          data: data.map((record: Transaction) => {
            if (getDisplayText(record.record_id) === targetId) {
              return { ...record, [field]: value }
            }
            return record
          }),
        }
      }),
    }
  })
}

export function usePatchTransaction(
  workspaceId: string,
  workerId: string,
  apiFetch: ApiFetch = fetch,
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ recordId, field, value }: PatchOp) => {
      assertFinancialPatchAllowed(field, value)
      const res = await apiFetch(`/api/v1/transactions/${recordId}?workspace_id=${workspaceId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields: [{ field, value }] }),
      })
      if (!res.ok) {
        let detail = ''
        try {
          const body = await res.json()
          detail =
            (typeof body?.detail === 'string' && body.detail) ||
            (typeof body?.error === 'string' && body.error) ||
            (typeof body?.message === 'string' && body.message) ||
            ''
        } catch {
          try {
            detail = (await res.text()).slice(0, 240)
          } catch {
            detail = ''
          }
        }
        throw new Error(
          detail.trim()
            ? `บันทึกไม่สำเร็จ (${res.status}): ${detail.trim()}`
            : `บันทึกไม่สำเร็จ (${res.status})`,
        )
      }
      return res.json()
    },
    onMutate: async ({ recordId, field, value }) => {
      assertFinancialPatchAllowed(field, value)
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['transactions', workerId, workspaceId] })

      // Snapshot the previous value
      const previousQueries = queryClient.getQueriesData({ queryKey: ['transactions', workerId, workspaceId] })

      // Optimistically update
      applyOptimisticPatch(queryClient, workspaceId, workerId, { recordId, field, value })

      // Return a context object with the snapshotted value
      return { previousQueries }
    },
    onError: (_err, _newTx, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      // You would typically call toast.error('บันทึกไม่สำเร็จ ลองใหม่') here
    },
    onSettled: () => {
      // Always refetch after error or success to sync with server
      // We don't invalidate immediately here to avoid UI jumps, but we could
      queryClient.invalidateQueries({ queryKey: ['transactions', workerId, workspaceId] })
    },
  })
}
